/**
 * Shared file-type gate for the two bulk-file entry points that a customer's
 * part list can arrive through — the Cart's "Upload a CSV" control
 * (`CsvImportControl`) and the CAD/BOM upload widget (`useUploads`) — so the
 * SAME file is accepted or rejected the same way, with the same clear message,
 * in both. Before this existed the two diverged: the CAD widget rejected an
 * `.xlsx` generically ("None of the selected files could be prepared for
 * upload") while the CSV control silently read the binary as text and flooded
 * the screen with per-row parse errors.
 *
 * Extension-based on purpose: the browser's reported MIME type for a `.csv` is
 * notoriously inconsistent (Windows reports `application/vnd.ms-excel`), so the
 * filename extension is the reliable signal here. The server now applies the
 * SAME reasoning — `upload-policy.ts`'s `TEXT_TABULAR_CONTENT_TYPES` pair check
 * (ADR-0134 D6) treats the extension as authoritative and the MIME as merely
 * corroborating, precisely so `application/vnd.ms-excel` on a `.csv` is admitted
 * while the same MIME on a real `.xls` is not.
 *
 * The two callers share the WORKBOOK gate ({@link isSpreadsheetWorkbook} +
 * {@link SPREADSHEET_UNSUPPORTED_MESSAGE}), which is the part that must never
 * drift. They deliberately differ beyond it: the CSV control is text-only and
 * uses {@link csvImportRejection}, whereas the CAD/BOM widget also accepts CAD
 * and drawing formats, so it applies only the workbook gate (ADR-0134 D7).
 */

/** Spreadsheet workbooks we do NOT parse yet (as opposed to plain-text CSV/TSV).
 * `.xlsx` is the common culprit — an Excel export the customer forgot to "Save
 * As CSV". */
const SPREADSHEET_EXT = /\.(xlsx|xlsm|xlsb|xls|ods|numbers)$/i

/** Plain-text tabular formats the CSV parser can actually read. */
const TEXT_EXT = /\.(csv|tsv|tab|txt)$/i

/** Any filename extension at all (used to tell "no extension" from "some other
 * binary extension"). */
const HAS_EXT = /\.[^.\\/]+$/

/**
 * The single, shared message for an unsupported spreadsheet workbook — used by
 * BOTH entry points so the wording never drifts. Deliberately actionable: it
 * names the two supported ways in (save as CSV/TSV, or paste).
 */
export const SPREADSHEET_UNSUPPORTED_MESSAGE =
  'Excel and other spreadsheet files aren’t supported yet — save it as CSV (or TSV), or use “Copy from Excel” to paste the rows.'

/** Message for a non-spreadsheet binary handed to a TEXT-only importer. */
export const NON_TEXT_UNSUPPORTED_MESSAGE =
  'Only CSV or TSV files are supported. Save your spreadsheet as CSV, or use “Copy from Excel” to paste the rows.'

/** True when the file is a spreadsheet workbook (`.xlsx`, `.xls`, `.ods`, …).
 * Both entry points reject these with {@link SPREADSHEET_UNSUPPORTED_MESSAGE}. */
export function isSpreadsheetWorkbook(file: { readonly name: string }): boolean {
  return SPREADSHEET_EXT.test(file.name)
}

/**
 * Rejection reason for a file handed to a TEXT-only CSV/TSV importer, or `null`
 * when the file is acceptable. Spreadsheets get the shared workbook message;
 * any other non-text extension (e.g. `.pdf`, `.step`, `.zip`) gets the
 * text-only message; `.csv`/`.tsv`/`.tab`/`.txt` — and a file with no
 * extension, which is treated as text — are accepted.
 */
export function csvImportRejection(file: { readonly name: string }): string | null {
  if (isSpreadsheetWorkbook(file)) return SPREADSHEET_UNSUPPORTED_MESSAGE
  if (TEXT_EXT.test(file.name) || !HAS_EXT.test(file.name)) return null
  return NON_TEXT_UNSUPPORTED_MESSAGE
}
